import { FormEvent, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { StyledDefaultPrimaryButton, StyledSimpleCard } from 'styles';
import { Cohort, cohortHasRoom } from 'TenantModule/api/getCohortsApi';
import { useAllCohortsQuery } from 'TenantModule/api/queries/useAllCohortsQuery';
import { useCohortMutations } from 'TenantModule/api/queries/useCohortMutations';

const DEDICATED_CAPACITY = 1;
// The statuses this card knows how to set. The API types status as a string, so a
// server-side widening of the enum (or a partial rollout emitting a wider vocabulary)
// must degrade to a read-only display rather than a blank control that silently
// overwrites the value on the next click.
const EDITABLE_STATUSES: string[] = ['active', 'archived'];
// An Enterprise cohort must keep at least 2 seats: dropping to 1 would make it
// indistinguishable from a dedicated cohort-of-one — the row would render read-only
// (unrecoverable from this UI) and the server would stop offering it in the
// group-filling picker while a tenant is still seated in it. Matches the server's
// CreateCohortDto @Min(2).
const DEFAULT_REGION = 'us-east-1';
const MIN_ENTERPRISE_CAPACITY = 2;
// Matches the registration form's 2..50 range and the server's @Max(50): without it
// the stepper can drive an existing cohort past a limit new cohorts cannot be
// created at, and the request comes straight back as an error toast.
const MAX_ENTERPRISE_CAPACITY = 50;

/**
 * Cohort administration (RDM-2031 resolved Q5): register and edit the shared-cluster
 * pools behind Enterprise group filling from Galaxy instead of raw SQL.
 *
 * The cluster itself is IaC-owned — registering a cohort only points Galaxy at the
 * Pulumi-published master secret(s) of a cluster infra has already created.
 * Dedicated cohorts-of-one (capacity 1, created implicitly for Single/Shared
 * tenants) are listed read-only: they can never take another tenant.
 */
export const CohortManagementCard = () => {
  const { cohorts, isLoading, isError } = useAllCohortsQuery();
  const [showDedicated, setShowDedicated] = useState(false);
  const { createCohort, isCreatingCohort, updateCohort, isUpdatingCohort } = useCohortMutations();

  const [applicationClusterSecret, setApplicationClusterSecret] = useState('');
  const [integrationClusterSecret, setIntegrationClusterSecret] = useState('');
  const [capacityLimit, setCapacityLimit] = useState('5');
  const [region, setRegion] = useState(DEFAULT_REGION);

  const parsedCapacity = Number(capacityLimit);
  // Same constants the table steppers enforce, so the two paths cannot drift apart
  // from each other or from the server DTO's @Min/@Max.
  const isCapacityValid =
    Number.isInteger(parsedCapacity) &&
    parsedCapacity >= MIN_ENTERPRISE_CAPACITY &&
    parsedCapacity <= MAX_ENTERPRISE_CAPACITY;
  const canSubmit =
    applicationClusterSecret.trim().length > 0 && isCapacityValid && !isCreatingCohort;

  const onRegister = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    createCohort(
      {
        applicationClusterSecret: applicationClusterSecret.trim(),
        ...(integrationClusterSecret.trim()
          ? { integrationClusterSecret: integrationClusterSecret.trim() }
          : {}),
        capacityLimit: parsedCapacity,
        region: region.trim() || undefined,
      },
      {
        onSuccess: () => {
          setApplicationClusterSecret('');
          setIntegrationClusterSecret('');
          setCapacityLimit('5');
          // Region too, or the next cohort silently inherits the last one's region.
          setRegion(DEFAULT_REGION);
        },
      },
    );
  };

  const isDedicated = (cohort: Cohort) => cohort.capacityLimit === DEDICATED_CAPACITY;
  const dedicatedCount = cohorts.filter(isDedicated).length;
  const visibleCohorts = showDedicated ? cohorts : cohorts.filter((c) => !isDedicated(c));

  return (
    <StyledSimpleCard>
      <h3>Enterprise Cohorts</h3>
      <Box sx={{ mb: 2, opacity: 0.75 }}>
        A cohort is a set of tenants sharing one Aurora cluster. Create the cluster in mosaic-infra
        first, then register it here with its master-secret name(s).
      </Box>

      <form onSubmit={onRegister}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mb: 3,
          }}
        >
          <TextField
            required
            label="Application cluster secret"
            helperText="AWS Secrets Manager name, e.g. enterprise-1-cluster"
            value={applicationClusterSecret}
            onChange={(e) => setApplicationClusterSecret(e.target.value)}
            disabled={isCreatingCohort}
          />
          <TextField
            label="Integration cluster secret (optional)"
            helperText="Leave blank to run integration workloads on the application cluster"
            value={integrationClusterSecret}
            onChange={(e) => setIntegrationClusterSecret(e.target.value)}
            disabled={isCreatingCohort}
          />
          <TextField
            label="Capacity"
            type="number"
            value={capacityLimit}
            onChange={(e) => setCapacityLimit(e.target.value)}
            error={!isCapacityValid}
            helperText={
              isCapacityValid
                ? 'Seats in this cohort (default 5)'
                : `Capacity must be a whole number between ${MIN_ENTERPRISE_CAPACITY} and ${MAX_ENTERPRISE_CAPACITY}`
            }
            disabled={isCreatingCohort}
          />
          <TextField
            label="Region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={isCreatingCohort}
          />
          <StyledDefaultPrimaryButton type="submit" disabled={!canSubmit}>
            Register cohort
          </StyledDefaultPrimaryButton>
        </Box>
      </form>

      {isError ? (
        <Box>Failed to load cohorts.</Box>
      ) : isLoading ? (
        <Box>Loading cohorts…</Box>
      ) : (
        <>
          {/* Every tenant owns a dedicated cohort-of-one, so this list grows with the
              TENANT count, not the Enterprise-cohort count — party already carries 52
              rows, all dedicated. They are read-only here, so showing them by default
              buries the handful of rows this card exists to administer. */}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showDedicated}
                onChange={(e) => setShowDedicated(e.target.checked)}
                slotProps={{
                  input: { 'aria-label': 'show dedicated cohorts' },
                }}
              />
            }
            label={`Show dedicated cohorts (${dedicatedCount})`}
          />
          <Table size="small" data-testid="cohort-admin-table">
            <TableHead>
              <TableRow>
                <TableCell>Cohort</TableCell>
                <TableCell>Fill</TableCell>
                <TableCell>Application cluster</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Capacity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCohorts.map((cohort) => (
                <TableRow key={cohort.id}>
                  <TableCell>
                    {cohort.cohortId}
                    {isDedicated(cohort) ? (
                      <Chip size="small" label="dedicated" sx={{ ml: 1 }} />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {`${cohort.currentCount}/${cohort.capacityLimit}`}
                    {!cohortHasRoom(cohort) && !isDedicated(cohort) ? (
                      <Chip size="small" color="warning" label="full" sx={{ ml: 1 }} />
                    ) : null}
                  </TableCell>
                  <TableCell>{cohort.applicationClusterSecret}</TableCell>
                  <TableCell>
                    {isDedicated(cohort) || !EDITABLE_STATUSES.includes(cohort.status) ? (
                      // A status this UI cannot edit is still shown. MUI blanks a Select
                      // whose value has no MenuItem, and any subsequent click would then
                      // write 'active'/'archived' over a state the operator never saw.
                      cohort.status
                    ) : (
                      <Select
                        size="small"
                        value={cohort.status}
                        disabled={isUpdatingCohort}
                        onChange={(e) =>
                          // Coerced: the API contract is a string, and MUI's change
                          // value type depends on the Select's generic.
                          updateCohort({ id: cohort.id, body: { status: String(e.target.value) } })
                        }
                        inputProps={{ 'aria-label': `status for ${cohort.cohortId}` }}
                      >
                        <MenuItem value="active">active</MenuItem>
                        <MenuItem value="archived">archived</MenuItem>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isDedicated(cohort) ? (
                      cohort.capacityLimit
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          justifyContent: 'flex-end',
                        }}
                      >
                        {/* Capacity is tuned per cohort (e.g. dropped for a noisy
                          neighbour); the server rejects going below the live count. */}
                        <Button
                          size="small"
                          disabled={
                            isUpdatingCohort ||
                            cohort.capacityLimit <=
                              Math.max(cohort.currentCount, MIN_ENTERPRISE_CAPACITY)
                          }
                          onClick={() =>
                            updateCohort({
                              id: cohort.id,
                              body: { capacityLimit: cohort.capacityLimit - 1 },
                            })
                          }
                          aria-label={`decrease capacity for ${cohort.cohortId}`}
                        >
                          −
                        </Button>
                        <span>{cohort.capacityLimit}</span>
                        <Button
                          size="small"
                          disabled={
                            isUpdatingCohort || cohort.capacityLimit >= MAX_ENTERPRISE_CAPACITY
                          }
                          onClick={() =>
                            updateCohort({
                              id: cohort.id,
                              body: { capacityLimit: cohort.capacityLimit + 1 },
                            })
                          }
                          aria-label={`increase capacity for ${cohort.cohortId}`}
                        >
                          +
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </StyledSimpleCard>
  );
};
